import {
  DatePickerInput,
  Input,
  SkeletonLoader,
  useValidation,
  DateFormat,
  formatDate,
  noop,
  sizing,
} from '@chillibean/component-library';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { StudyProtocol } from '../../models';
import { setProtocolAction, setProtocolUpdated } from '../../redux/actions';
import { isFieldError, transformFieldErrors } from '../../utils';
import { FooterButtons } from './FooterButtons';

export function ProtocolFormLoading() {
  return (
    <Wrapper>
      <Fields>
        <SkeletonLoader
          height={48}
          preserveAspectRatio="none"
          style={{ borderRadius: sizing.xsmall, width: '100%' }}
          width={100}
        >
          <rect height={48} rx={0} ry={0} width="100%" x="0" y="0" />
        </SkeletonLoader>
        <SkeletonLoader
          height={48}
          preserveAspectRatio="none"
          style={{ borderRadius: sizing.xsmall, width: '100%' }}
          width={100}
        >
          <rect height={48} rx={0} ry={0} width="100%" x="0" y="0" />
        </SkeletonLoader>
      </Fields>
    </Wrapper>
  );
}

export function ProtocolForm(props) {
  const { loading = false, locked = false, onSave = noop, protocol = undefined } = props;

  // VARIABLES
  const defaultValues = {
    issueDate: protocol?.issueDate && formatDate(protocol?.issueDate, DateFormat.ISO_DATE),
    name: protocol?.name,
  };
  const newStudyProtocol = !protocol;

  // Store
  const dispatch = useDispatch();
  const { error: actionError, loading: actionLoading } = useSelector((state) => state.protocolReducer.protocolAction);
  // Form
  const {
    formState: { errors, isDirty },
    getValues,
    register,
    reset,
    setError,
    setValue,
    trigger,
    watch,
  } = useForm();

  // Custom
  const { validationMessages } = useValidation();

  const initializeForm = () => {
    register('name', {
      defaultValue: defaultValues.name,
      maxLength: {
        message: validationMessages.maxLength(250),
        value: 250,
      },
      required: {
        message: validationMessages.required(),
        value: true,
      },
      value: defaultValues.name,
    });
    register('issueDate', {
      defaultValue: defaultValues.name,
      required: {
        message: validationMessages.required(),
        value: true,
      },
      value: defaultValues.issueDate,
    });
  };

  const setFieldsError = () => {
    const fieldError = isFieldError(actionError);
    if (!fieldError) return;

    const fieldErrors = transformFieldErrors(actionError);
    if (!fieldErrors) return;

    Object.entries(fieldErrors).forEach(([fieldName, error]) => {
      setError(fieldName, {
        message: validationMessages[error.type](error.value),
      });
    });
  };

  useEffect(() => {
    reset(defaultValues);
    initializeForm();

    return () => dispatch(setProtocolAction({ error: null, loading: false }));
  }, [protocol]);

  useEffect(() => {
    dispatch(setProtocolUpdated({ data: isDirty }));
  }, [isDirty]);

  useEffect(() => {
    setFieldsError();
  }, [actionError]);

  const save = async () => {
    const valid = await trigger();

    if (valid) {
      onSave(getValues());
    }
  };

  const formButtons = () => {
    const buttons = [];
    if (!locked) {
      buttons.push({
        handleClick: save,
        isDisabled: actionLoading,
        loading: actionLoading,
        name: newStudyProtocol ? 'Create Trial Configuration' : 'Save Trial Configuration',
      });
    }
    return buttons;
  };
  const backButton = {
    isDisabled: actionLoading,
    name: newStudyProtocol ? 'Cancel' : 'Back',
  };

  const handleUpdateValue = (key, value) => {
    setValue(key, value, { shouldDirty: true });
  };

  if (loading) return <ProtocolFormLoading />;

  return (
    <Wrapper>
      <Fields>
        <Input
          disabled={locked || actionLoading}
          error={errors.name?.message}
          id="name"
          label="Trial Configuration"
          name="Trial Configuration"
          onChange={(event) => handleUpdateValue('name', event.target.value)}
          required
          value={watch('name')}
        />
        <DatePickerInput
          disabled={locked || actionLoading}
          error={errors.issueDate?.message}
          id="issueDate"
          label="Issue Date"
          name="Issue Date"
          onChange={(event) => handleUpdateValue('issueDate', event.target.value)}
          required
          value={watch('issueDate')}
        />
      </Fields>
      <FooterButtons backButton={backButton} buttons={formButtons()} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizing.medium};
  padding-bottom: 4.5rem;

  // ! override default CP styles
  .error {
    margin: 0;
    font-size: inherit;
  }
`;

ProtocolForm.propTypes = {
  loading: PropTypes.bool,
  locked: PropTypes.bool,
  onSave: PropTypes.func,
  protocol: PropTypes.instanceOf(StudyProtocol),
};

export default ProtocolForm;
